import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import type { AnalysisRecommendation, AnalysisSnapshot } from '../../../types/analysis'
import { MetricCard } from '../MetricCard'

import { buildMetricDescriptor, buildScoreRingDescriptor } from '../popover/descriptors'

import {
  AiSummaryHero,
  buildCompositionDescriptor,
  buildMultiSeriesDescriptor,
  CompositionDonut,
  formatNumber,
  MultiSeriesChart,
  RecommendationsFooter,
  ScoreRing,
  TrendStrip,
} from './analysisTabShared'
import type { AnalysisNavigate } from './analysisTabShared'

export const OverviewTab = ({ snapshot, summary, onNavigate, onOpenReports, variant = 'default' }: { snapshot: AnalysisSnapshot; summary: string | null; onNavigate: AnalysisNavigate; onOpenReports: () => void; variant?: 'default' | 'member-dense' }) => {
  const { t } = useNls()
  const scoreDescriptor = buildScoreRingDescriptor({
    snapshot,
    onNavigate,
    onOpenReports,
  })
  const multiSeriesDescriptor = buildMultiSeriesDescriptor({
    snapshot,
    onOpenReports,
  })
  const compositionDescriptor = buildCompositionDescriptor({
    snapshot,
    onOpenReports,
  })
  const metrics: Array<{
    title: string
    value: string
    detail: string
    icon: ReactNode
    tone?: 'default' | 'warn' | 'good'
    numeric: number
    drillTarget: AnalysisRecommendation['drillTarget'] | null
  }> = [
    {
      title: 'Completion',
      value: `${formatNumber(snapshot.work.completed)} / ${formatNumber(snapshot.work.total)}`,
      detail: 'All active workspace records',
      icon: <TrackChangesRoundedIcon />,
      tone: 'good',
      numeric: snapshot.work.completed,
      drillTarget: 'DASHBOARD',
    },
    {
      title: 'Blocked',
      value: formatNumber(snapshot.work.blocked),
      detail: 'Dependency or gate pressure',
      icon: <WarningAmberRoundedIcon />,
      tone: snapshot.work.blocked ? 'warn' : 'good',
      numeric: snapshot.work.blocked,
      drillTarget: 'CHECKLISTS',
    },
    {
      title: 'Proof gates',
      value: formatNumber(snapshot.ops.proofRequired),
      detail: 'Evidence required before completion',
      icon: <CloudUploadRoundedIcon />,
      numeric: snapshot.ops.proofRequired,
      drillTarget: 'UPLOAD',
    },
    {
      title: 'Budget touched',
      value: formatNumber(snapshot.ops.budgeted),
      detail: 'Items with cost signals',
      icon: <PaidRoundedIcon />,
      numeric: snapshot.ops.budgeted,
      drillTarget: 'BUDGET',
    },
  ]
  const metricGrid = (
    <div className="analysisMetricGrid">
      {metrics.map((metric) => (
        <MetricCard
          key={metric.title}
          title={metric.title}
          value={metric.value}
          detail={metric.detail}
          icon={metric.icon}
          tone={metric.tone}
          descriptor={buildMetricDescriptor({
            snapshot,
            tab: 'OVERVIEW',
            title: metric.title,
            value: metric.numeric,
            detail: metric.detail,
            drillTarget: metric.drillTarget,
            onNavigate,
            onOpenReports,
          })}
        />
      ))}
    </div>
  )

  if (variant === 'member-dense') {
    return (
      <div className="analysisStageStack analysisStageStack--memberDense">
        <ScoreRing snapshot={snapshot} descriptor={scoreDescriptor} memberDense healthComponentsLabel={t('NLSAnalysisMobileHealthComponents')} scoreScaleLabel={t('NLSAnalysisMobileScoreOutOf100')} />
        <MultiSeriesChart trends={snapshot.trends} descriptor={multiSeriesDescriptor} />
        {metricGrid}
        <AiSummaryHero snapshot={snapshot} summary={summary} />
        <CompositionDonut byType={snapshot.work.byType} total={snapshot.work.total} descriptor={compositionDescriptor} />
        <TrendStrip trends={snapshot.trends} snapshot={snapshot} onOpenReports={onOpenReports} />
        <RecommendationsFooter recommendations={snapshot.recommendations} onNavigate={onNavigate} />
      </div>
    )
  }

  return (
    <div className="analysisStageStack">
      <div className="analysisHeroGrid">
        <ScoreRing snapshot={snapshot} descriptor={scoreDescriptor} />
        <AiSummaryHero snapshot={snapshot} summary={summary} />
      </div>
      <div className="analysisChartGrid">
        <MultiSeriesChart trends={snapshot.trends} descriptor={multiSeriesDescriptor} />
        <CompositionDonut byType={snapshot.work.byType} total={snapshot.work.total} descriptor={compositionDescriptor} />
      </div>
      {metricGrid}
      <TrendStrip trends={snapshot.trends} snapshot={snapshot} onOpenReports={onOpenReports} />
      <RecommendationsFooter recommendations={snapshot.recommendations} onNavigate={onNavigate} />
    </div>
  )
}
